import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

const PARAM = "setupSource";

/**
 * Reads `?setupSource=...` from the URL on mount. If present, calls the
 * provided handler exactly once with the value + the current pathname,
 * then strips the param so a refresh doesn't re-trigger the start flow.
 *
 * The pathname is forwarded so the consumer can route-match the URL to a
 * registered product (e.g. `/finance/disputes/...` → disputes), keeping
 * the entry path product-agnostic.
 *
 * Use cases:
 *   - `?setupSource=shopify_reclaim_install` — direct Shopify Reclaim install lands here
 *   - `?setupSource=homepage_card` — clicked the cross-sell card on the homepage
 *   - `?setupSource=email_cta` — clicked an email CTA
 *   - `?setupSource=widget` — clicked the cross-route widget reminder
 *
 * Hook is responsible for one-shot consumption + URL cleanup. The caller
 * (provider) decides what to do with the source string (typically: call
 * startMerchantSetupProgress with it as `entrySource`).
 */
export function useEntrySourceParam(
  onConsume: (source: string, pathname: string) => void,
): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    const value = searchParams.get(PARAM);
    if (!value) return;
    consumedRef.current = true;

    onConsume(value, location.pathname);

    const next = new URLSearchParams(searchParams);
    next.delete(PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, location.pathname, onConsume]);
}
